import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { CurrencyDisplay } from "./CurrencySettings";
import { 
  FileSpreadsheet, 
  Download, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Receipt,
  Crown,
  ChevronRight,
  Info,
  Calculator,
  Minus,
  Equal,
  Eye,
  Users,
  CreditCard,
  Building2,
  Filter,
  FileText,
  BookOpen,
  X
} from "lucide-react";
import DateFilter from "./DateFilter";
import * as XLSX from 'xlsx';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { formatDateDDMonYYYY } from "../utils/dateFormat";

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ProfitLossReport = ({ restaurants }) => {
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({
    total_revenue: 0, total_expenses: 0, staff_wages: 0,
    expenses_excl_wages: 0, platform_fees: 0, head_office_expenses: 0,
    gross_profit: 0, profit_margin: 0, total_royalties: 0,
    net_profit: 0, net_profit_margin: 0
  });
  const [breakdowns, setBreakdowns] = useState({
    revenue: [], expense: [], royalty: [], staff_wages: [], platform_deductions: [], head_office_expenses: []
  });
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [brands, setBrands] = useState([]);
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailType, setDetailType] = useState("revenue");
  const [selectedRestaurantDetail, setSelectedRestaurantDetail] = useState(null);

  // Dialog filters
  const [dialogRestaurant, setDialogRestaurant] = useState("all");
  const [dialogCategory, setDialogCategory] = useState("all");
  const [dialogMonthFrom, setDialogMonthFrom] = useState("");
  const [dialogMonthTo, setDialogMonthTo] = useState("");

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get("/brands");
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedRestaurant !== "all") params.append("restaurant_id", selectedRestaurant);
      if (selectedBrand !== "all") params.append("brand_id", selectedBrand);

      const response = await axiosInstance.get(`/profit-loss-report?${params.toString()}`);
      setReportData(response.data.items);
      setSummary(response.data.summary);
      setBreakdowns({
        revenue: response.data.revenue_breakdown || [],
        expense: response.data.expense_breakdown || [],
        royalty: response.data.royalty_breakdown || [],
        staff_wages: response.data.staff_wages_breakdown || [],
        platform_deductions: response.data.platform_deduction_breakdown || [],
        head_office_expenses: response.data.head_office_expense_breakdown || []
      });
      toast.success("Report generated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const openDetailDialog = (type, restaurantName = null) => {
    setDetailType(type);
    setSelectedRestaurantDetail(restaurantName);
    setDialogRestaurant(restaurantName || "all");
    setDialogCategory("all");
    setDialogMonthFrom("");
    setDialogMonthTo("");
    setDetailDialog(true);
  };

  const getFilteredBreakdown = (type) => {
    let data = breakdowns[type] || [];

    // Restaurant filter
    if (dialogRestaurant && dialogRestaurant !== "all") {
      data = data.filter(item => item.restaurant_name === dialogRestaurant);
    }

    // Category filter (field varies by type)
    if (dialogCategory && dialogCategory !== "all") {
      data = data.filter(item => {
        if (type === "revenue" || type === "expense" || type === "staff_wages") return item.category_name === dialogCategory;
        if (type === "platform_deductions") return item.platform_name === dialogCategory;
        if (type === "royalty") return item.royalty_type === dialogCategory || item.payee_name === dialogCategory;
        if (type === "head_office_expenses") return item.category === dialogCategory;
        return true;
      });
    }

    // Month range filter
    if (dialogMonthFrom || dialogMonthTo) {
      data = data.filter(item => {
        let monthKey = "";
        if (item.date) {
          monthKey = String(item.date).substring(0, 7);
        } else if (item.month !== undefined && item.year !== undefined) {
          monthKey = `${item.year}-${String(item.month).padStart(2, '0')}`;
        } else if (typeof item.month === 'string') {
          monthKey = item.month.substring(0, 7);
        }
        if (dialogMonthFrom && monthKey < dialogMonthFrom) return false;
        if (dialogMonthTo && monthKey > dialogMonthTo) return false;
        return true;
      });
    }
    return data;
  };

  // Get unique values for filter dropdowns from active tab data
  const getDialogFilterOptions = (type) => {
    const data = breakdowns[type] || [];
    const restaurants = [...new Set(data.map(d => d.restaurant_name).filter(Boolean))].sort();
    let categories = [];
    if (type === "revenue" || type === "expense" || type === "staff_wages") {
      categories = [...new Set(data.map(d => d.category_name).filter(Boolean))].sort();
    } else if (type === "platform_deductions") {
      categories = [...new Set(data.map(d => d.platform_name).filter(Boolean))].sort();
    } else if (type === "royalty") {
      categories = [...new Set(data.map(d => d.payee_name).filter(Boolean))].sort();
    } else if (type === "head_office_expenses") {
      categories = [...new Set(data.map(d => d.category).filter(Boolean))].sort();
    }
    const months = [...new Set(data.map(d => {
      if (d.date) return String(d.date).substring(0, 7);
      if (d.month !== undefined && d.year !== undefined && typeof d.month === 'number') return `${d.year}-${String(d.month).padStart(2, '0')}`;
      if (typeof d.month === 'string') return d.month.substring(0, 7);
      return "";
    }).filter(Boolean))].sort();
    return { restaurants, categories, months };
  };

  const getCategoryLabel = (type) => {
    if (type === "platform_deductions") return "Platform";
    if (type === "royalty") return "Payee";
    if (type === "head_office_expenses") return "Category";
    return "Category";
  };

  const clearDialogFilters = () => {
    setDialogRestaurant(selectedRestaurantDetail || "all");
    setDialogCategory("all");
    setDialogMonthFrom("");
    setDialogMonthTo("");
  };

  const hasActiveDialogFilters = () => {
    const defaultRest = selectedRestaurantDetail || "all";
    return dialogRestaurant !== defaultRest || dialogCategory !== "all" || dialogMonthFrom || dialogMonthTo;
  };

  const TAB_LABELS = {
    revenue: "Revenue", expense: "Expenses", staff_wages: "Staff Wages",
    platform_deductions: "Platform Fees", royalty: "Royalty", head_office_expenses: "HO Expenses"
  };

  const downloadDialogExcel = (type) => {
    const data = getFilteredBreakdown(type);
    if (data.length === 0) { toast.error("No data to export"); return; }

    const rows = data.map(item => {
      const row = {};
      if (item.date) row['Date'] = formatDateDDMonYYYY(item.date);
      else if (item.month) { const [y, m] = (item.month || '').split('-'); row['Month'] = m ? `${MONTHS[parseInt(m)]}-${y}` : item.month; }
      if (item.restaurant_name) row['Restaurant'] = item.restaurant_name;
      if (item.category_name) row['Category'] = item.category_name;
      if (item.platform_name) row['Platform'] = item.platform_name;
      if (item.payee_name) row['Payee'] = item.payee_name;
      if (item.category && type === "head_office_expenses") row['Category'] = item.category;
      if (item.vendor_name) row['Vendor'] = item.vendor_name;
      if (item.description) row['Description'] = item.description;
      if (item.royalty_type) row['Type'] = item.royalty_type;
      row['Amount'] = (item.amount || 0).toFixed(2);
      return row;
    });

    const total = data.reduce((s, i) => s + (i.amount || 0), 0);
    const totalRow = {};
    Object.keys(rows[0]).forEach(k => totalRow[k] = '');
    totalRow[Object.keys(rows[0])[0]] = 'TOTAL';
    totalRow['Amount'] = total.toFixed(2);
    rows.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, TAB_LABELS[type]);
    XLSX.writeFile(wb, `PL_${TAB_LABELS[type].replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel downloaded!");
  };

  const downloadDialogPDF = (type) => {
    const data = getFilteredBreakdown(type);
    if (data.length === 0) { toast.error("No data to export"); return; }

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const fmtCurrency = (val) => `Rs. ${Number(val).toLocaleString('en-IN')}`;
    const label = TAB_LABELS[type];
    const total = data.reduce((s, i) => s + (i.amount || 0), 0);

    // Header
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(`P&L Breakdown: ${label}`, 14, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    let periodText = "Period: ";
    if (startDate && endDate) periodText += `${formatDateDDMonYYYY(startDate)} to ${formatDateDDMonYYYY(endDate)}`;
    else periodText += "All Time";
    if (dialogRestaurant !== "all") periodText += ` | Restaurant: ${dialogRestaurant}`;
    if (dialogCategory !== "all") periodText += ` | ${getCategoryLabel(type)}: ${dialogCategory}`;
    doc.text(periodText, 14, 28);
    doc.text("N Square Cafe", pageWidth - 14, 18, { align: 'right' });

    // Summary cards
    const startY = 45;
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, startY, 80, 20, 2, 2, 'F');
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.8);
    doc.line(14, startY, 14, startY + 20);
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Total ${label}`, 19, startY + 7);
    doc.setFontSize(14);
    doc.setTextColor(59, 130, 246);
    doc.setFont(undefined, 'bold');
    doc.text(fmtCurrency(total), 19, startY + 17);

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(100, startY, 80, 20, 2, 2, 'F');
    doc.setDrawColor(34, 197, 94);
    doc.line(100, startY, 100, startY + 20);
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont(undefined, 'normal');
    doc.text("Entries", 105, startY + 7);
    doc.setFontSize(14);
    doc.setTextColor(34, 197, 94);
    doc.setFont(undefined, 'bold');
    doc.text(`${data.length}`, 105, startY + 17);

    // Table
    const cols = [];
    const headRow = [];
    const bodyData = data.map(item => {
      const row = [];
      if (item.date) { if (!cols.includes('Date')) cols.push('Date'); row.push(formatDateDDMonYYYY(item.date)); }
      else if (item.month) { if (!cols.includes('Month')) cols.push('Month'); const [y, m] = (item.month || '').split('-'); row.push(m ? `${MONTHS[parseInt(m)]}-${y}` : item.month); }
      if (item.restaurant_name) { if (!cols.includes('Restaurant')) cols.push('Restaurant'); row.push(item.restaurant_name); }
      if (item.category_name) { if (!cols.includes('Category')) cols.push('Category'); row.push(item.category_name); }
      if (item.platform_name) { if (!cols.includes('Platform')) cols.push('Platform'); row.push(item.platform_name); }
      if (item.payee_name) { if (!cols.includes('Payee')) cols.push('Payee'); row.push(item.payee_name); }
      if (item.category && type === "head_office_expenses") { if (!cols.includes('Category')) cols.push('Category'); row.push(item.category); }
      if (item.vendor_name) { if (!cols.includes('Vendor')) cols.push('Vendor'); row.push(item.vendor_name); }
      if (item.royalty_type) { if (!cols.includes('Type')) cols.push('Type'); row.push(item.royalty_type); }
      if (!cols.includes('Amount')) cols.push('Amount');
      row.push(fmtCurrency(item.amount || 0));
      return row;
    });

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`${label} Details`, 14, startY + 33);

    autoTable(doc, {
      startY: startY + 36,
      head: [cols],
      body: bodyData,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { [cols.length - 1]: { halign: 'right', fontStyle: 'bold' } },
    });

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
      doc.text(`N Square Cafe - P&L ${label}`, 14, pageHeight - 8);
    }

    doc.save(`PL_${label.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF downloaded!");
  };

  const downloadExcel = () => {
    if (reportData.length === 0) { toast.error("No data to export"); return; }
    const data = reportData.map(item => ({
      'Restaurant': item.restaurant_name,
      'Brand': item.brand_name,
      'Revenue': item.total_revenue.toFixed(2),
      'Expenses (excl. Wages)': item.expenses_excl_wages.toFixed(2),
      'Staff Wages': item.staff_wages.toFixed(2),
      'Platform Fees': item.platform_fees.toFixed(2),
      'Gross Profit': item.gross_profit.toFixed(2),
      'Royalties': item.total_royalties.toFixed(2),
      'Net Profit': item.net_profit.toFixed(2),
      'Net %': item.net_profit_margin.toFixed(2)
    }));
    data.push({
      'Restaurant': 'TOTAL', 'Brand': '',
      'Revenue': summary.total_revenue.toFixed(2),
      'Expenses (excl. Wages)': summary.expenses_excl_wages.toFixed(2),
      'Staff Wages': summary.staff_wages.toFixed(2),
      'Platform Fees': summary.platform_fees.toFixed(2),
      'Gross Profit': summary.gross_profit.toFixed(2),
      'Royalties': summary.total_royalties.toFixed(2),
      'Net Profit': summary.net_profit.toFixed(2),
      'Net %': summary.net_profit_margin.toFixed(2)
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "P&L Report");
    XLSX.writeFile(wb, `PL_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel file downloaded!");
  };

  const downloadPDF = () => {
    if (reportData.length === 0) { toast.error("No data to export"); return; }
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const fmtCurrency = (val) => `Rs. ${Number(val).toLocaleString('en-IN')}`;

    // Header
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text("Profit & Loss Report", 14, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    let dateText = "Period: ";
    if (startDate && endDate) dateText += `${formatDateDDMonYYYY(startDate)} to ${formatDateDDMonYYYY(endDate)}`;
    else dateText += "All Time";
    doc.text(dateText, 14, 28);
    doc.text("N Square Cafe", pageWidth - 14, 18, { align: 'right' });

    // Summary cards
    const startY = 45;
    const cards = [
      { label: "Total Revenue", value: fmtCurrency(summary.total_revenue), color: [34, 197, 94] },
      { label: "Total Expenses", value: fmtCurrency(summary.expenses_excl_wages), color: [239, 68, 68] },
      { label: "Staff Wages", value: fmtCurrency(summary.staff_wages), color: [249, 115, 22] },
      { label: "Net Profit", value: fmtCurrency(summary.net_profit), color: summary.net_profit >= 0 ? [34, 197, 94] : [239, 68, 68] },
    ];
    const cw = 63, cg = 6;
    cards.forEach((card, i) => {
      const x = 14 + i * (cw + cg);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, startY, cw, 22, 2, 2, 'F');
      doc.setDrawColor(card.color[0], card.color[1], card.color[2]);
      doc.setLineWidth(0.8);
      doc.line(x, startY, x, startY + 22);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.setFont(undefined, 'normal');
      doc.text(card.label, x + 5, startY + 8);
      doc.setFontSize(13);
      doc.setTextColor(card.color[0], card.color[1], card.color[2]);
      doc.setFont(undefined, 'bold');
      doc.text(card.value, x + 5, startY + 18);
    });

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text("Restaurant-wise Breakdown", 14, startY + 35);

    const tableData = reportData.map(item => [
      item.restaurant_name, item.brand_name,
      fmtCurrency(item.total_revenue),
      fmtCurrency(item.expenses_excl_wages),
      fmtCurrency(item.staff_wages),
      fmtCurrency(item.platform_fees),
      fmtCurrency(item.total_royalties),
      fmtCurrency(item.net_profit),
      `${item.net_profit_margin}%`
    ]);
    autoTable(doc, {
      head: [['Restaurant', 'Brand', 'Revenue', 'Expenses', 'Staff Wages', 'Platform', 'Royalties', 'Net Profit', 'Net %']],
      body: tableData,
      startY: startY + 38,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
        5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right', fontStyle: 'bold' },
        8: { halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 8) {
          const val = parseFloat(data.cell.raw);
          data.cell.styles.textColor = val >= 0 ? [34, 197, 94] : [239, 68, 68];
        }
      }
    });

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
      doc.text("N Square Cafe - Profit & Loss Report", 14, pageHeight - 8);
    }

    doc.save(`PL_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF downloaded!");
  };

  const downloadDetailedPDF = () => {
    if (reportData.length === 0) { toast.error("Generate report first"); return; }

    const doc = new jsPDF('landscape');
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const fmtC = (v) => `Rs. ${Number(v || 0).toLocaleString('en-IN')}`;
    let periodLabel = "All Time";
    if (startDate && endDate) periodLabel = `${formatDateDDMonYYYY(startDate)} to ${formatDateDDMonYYYY(endDate)}`;

    // Helper: dark section header
    const drawSectionHeader = (title, subtitle) => {
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, pw, 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(title, 14, 16);
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(subtitle || periodLabel, 14, 24);
      doc.text("N Square Cafe", pw - 14, 16, { align: 'right' });
    };

    // Helper: draw a restaurant card
    const drawRestaurantCard = (x, y, w, h, name, total, items, accentColor) => {
      // Card background
      doc.setFillColor(250, 250, 252);
      doc.roundedRect(x, y, w, h, 2, 2, 'F');
      // Accent left border
      doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.setLineWidth(1.5);
      doc.line(x, y + 1, x, y + h - 1);
      // Restaurant name
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(name, x + 6, y + 9);
      // Total on the right
      doc.setFontSize(11);
      doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.text(fmtC(total), x + w - 6, y + 9, { align: 'right' });
      // Divider
      doc.setDrawColor(220, 220, 225);
      doc.setLineWidth(0.3);
      doc.line(x + 6, y + 13, x + w - 6, y + 13);
      // Category items
      let iy = y + 19;
      doc.setFontSize(8);
      items.forEach((item, idx) => {
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(item.name, x + 8, iy);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(55, 65, 81);
        doc.text(fmtC(item.amount), x + w - 8, iy, { align: 'right' });
        iy += 6;
      });
      return h;
    };

    // Helper: group breakdown by restaurant with category aggregation
    const groupByRestaurant = (data, catField) => {
      const groups = {};
      data.forEach(item => {
        const rName = item.restaurant_name || 'Unknown';
        if (!groups[rName]) groups[rName] = { total: 0, categories: {} };
        const catName = item[catField] || 'Other';
        groups[rName].categories[catName] = (groups[rName].categories[catName] || 0) + (item.amount || 0);
        groups[rName].total += (item.amount || 0);
      });
      return Object.entries(groups)
        .map(([name, data]) => ({
          name,
          total: data.total,
          items: Object.entries(data.categories).map(([n, a]) => ({ name: n, amount: a })).sort((a, b) => b.amount - a.amount)
        }))
        .sort((a, b) => b.total - a.total);
    };

    // Helper: render a section with restaurant cards (handles page breaks)
    const renderSection = (title, groups, accentColor) => {
      doc.addPage();
      drawSectionHeader(title, periodLabel);
      let curY = 38;
      const cardW = (pw - 42) / 2; // 2 cards per row
      let col = 0;

      if (groups.length === 0) {
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text("No data for this section", pw / 2, 60, { align: 'center' });
        return;
      }

      groups.forEach((group) => {
        const cardH = 18 + group.items.length * 6 + 4;
        // Check page break
        if (curY + cardH > ph - 20) {
          doc.addPage();
          drawSectionHeader(title + " (continued)", periodLabel);
          curY = 38;
          col = 0;
        }
        const x = 14 + col * (cardW + 14);
        drawRestaurantCard(x, curY, cardW, cardH, group.name, group.total, group.items, accentColor);

        if (col === 0) {
          col = 1;
        } else {
          col = 0;
          curY += cardH + 8;
        }
      });
    };

    // ==================== PAGE 1: Executive Summary ====================
    drawSectionHeader("P&L Detailed Report", `${periodLabel}  |  Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`);

    // Summary cards - 2 rows
    const summCards = [
      { label: "Total Revenue", value: summary.total_revenue, color: [34, 197, 94] },
      { label: "Expenses (excl. Wages)", value: summary.expenses_excl_wages, color: [239, 68, 68] },
      { label: "Staff Wages", value: summary.staff_wages, color: [249, 115, 22] },
      { label: "Platform Fees", value: summary.platform_fees, color: [234, 179, 8] },
      { label: "Royalties", value: summary.total_royalties, color: [147, 51, 234] },
      { label: "HO Expenses", value: summary.head_office_expenses, color: [99, 102, 241] },
      { label: "Net Profit", value: summary.net_profit, color: summary.net_profit >= 0 ? [34, 197, 94] : [239, 68, 68] },
      { label: "Net Margin", value: null, display: `${summary.net_profit_margin}%`, color: summary.net_profit_margin >= 0 ? [34, 197, 94] : [239, 68, 68] },
    ];

    const scW = 63, scG = 6, scY = 38;
    summCards.forEach((card, i) => {
      const row = Math.floor(i / 4);
      const col = i % 4;
      const x = 14 + col * (scW + scG);
      const y = scY + row * 28;
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(x, y, scW, 22, 2, 2, 'F');
      doc.setDrawColor(card.color[0], card.color[1], card.color[2]);
      doc.setLineWidth(0.8);
      doc.line(x, y, x, y + 22);
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.setFont(undefined, 'normal');
      doc.text(card.label, x + 5, y + 7);
      doc.setFontSize(12);
      doc.setTextColor(card.color[0], card.color[1], card.color[2]);
      doc.setFont(undefined, 'bold');
      doc.text(card.display || fmtC(card.value), x + 5, y + 17);
    });

    // Restaurant summary table
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text("Restaurant-wise Summary", 14, scY + 65);

    const tblData = reportData.map(item => [
      item.restaurant_name,
      fmtC(item.total_revenue),
      fmtC(item.expenses_excl_wages),
      fmtC(item.staff_wages),
      fmtC(item.platform_fees),
      fmtC(item.total_royalties),
      fmtC(item.net_profit),
      `${item.net_profit_margin}%`
    ]);

    autoTable(doc, {
      startY: scY + 68,
      head: [['Restaurant', 'Revenue', 'Expenses', 'Wages', 'Platform', 'Royalties', 'Net Profit', 'Net %']],
      body: tblData,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' },
        7: { halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && (data.column.index === 6 || data.column.index === 7)) {
          const val = parseFloat(String(data.cell.raw).replace(/[^0-9.-]/g, ''));
          data.cell.styles.textColor = val >= 0 ? [34, 197, 94] : [239, 68, 68];
        }
      }
    });

    // ==================== SECTION PAGES ====================
    const revGroups = groupByRestaurant(breakdowns.revenue, 'category_name');
    renderSection("Revenue Breakdown", revGroups, [34, 197, 94]);

    const expGroups = groupByRestaurant(breakdowns.expense, 'category_name');
    renderSection("Expenses Breakdown", expGroups, [239, 68, 68]);

    const wageGroups = groupByRestaurant(breakdowns.staff_wages, 'category_name');
    renderSection("Staff Wages Breakdown", wageGroups, [249, 115, 22]);

    const platGroups = groupByRestaurant(breakdowns.platform_deductions, 'platform_name');
    renderSection("Platform Fees Breakdown", platGroups, [234, 179, 8]);

    const royGroups = groupByRestaurant(breakdowns.royalty, 'payee_name');
    renderSection("Royalty Breakdown", royGroups, [147, 51, 234]);

    // HO Expenses — group by category (not restaurant)
    const hoData = breakdowns.head_office_expenses || [];
    const hoGroups = {};
    hoData.forEach(item => {
      const cat = item.category || 'Other';
      if (!hoGroups[cat]) hoGroups[cat] = { total: 0, items: [] };
      hoGroups[cat].total += item.amount || 0;
      hoGroups[cat].items.push({ name: item.description || item.month || '', amount: item.amount });
    });
    const hoCards = Object.entries(hoGroups).map(([name, d]) => ({
      name, total: d.total, items: d.items
    })).sort((a, b) => b.total - a.total);
    renderSection("Head Office Expenses", hoCards, [99, 102, 241]);

    // ==================== Footer on all pages ====================
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} of ${totalPages}`, pw - 14, ph - 6, { align: 'right' });
      doc.text("N Square Cafe - P&L Detailed Report", 14, ph - 6);
    }

    doc.save(`PL_Detailed_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Detailed PDF downloaded!");
  };

  const SummaryCard = ({ title, amount, icon: Icon, color, onClick }) => (
    <Card
      className={`shadow-md cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-l-4 ${color}`}
      onClick={onClick}
      data-testid={`pl-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 font-medium">{title}</p>
            <CurrencyDisplay amount={amount} className="text-lg font-bold text-gray-900" />
          </div>
          <Icon className="w-8 h-8 text-gray-300" />
        </div>
        <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
          <Eye className="w-3 h-3" /> View details
        </p>
      </CardContent>
    </Card>
  );

  const PLCalculationCard = () => (
    <Card className="shadow-xl bg-gradient-to-br from-slate-50 to-blue-50 border-0">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-600" />
          P&L Calculation
        </CardTitle>
        <CardDescription>Click on any value to see detailed breakdown</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Revenue */}
          <PLRow icon={TrendingUp} label="Total Revenue" amount={summary.total_revenue}
            bgColor="bg-green-50 hover:bg-green-100" textColor="text-green-600" iconColor="text-green-600"
            onClick={() => openDetailDialog('revenue')} clickable />

          <div className="flex items-center justify-center"><Minus className="w-4 h-4 text-gray-400" /></div>

          {/* Expenses (excl wages) */}
          <PLRow icon={Receipt} label="Expenses (excl. Staff Wages)" amount={summary.expenses_excl_wages}
            bgColor="bg-red-50 hover:bg-red-100" textColor="text-red-600" iconColor="text-red-600"
            onClick={() => openDetailDialog('expense')} clickable />

          <div className="flex items-center justify-center"><Minus className="w-4 h-4 text-gray-400" /></div>

          {/* Staff Wages */}
          <PLRow icon={Users} label="Staff Wages" amount={summary.staff_wages}
            bgColor="bg-orange-50 hover:bg-orange-100" textColor="text-orange-600" iconColor="text-orange-600"
            onClick={() => openDetailDialog('staff_wages')} clickable />

          <div className="flex items-center justify-center"><Minus className="w-4 h-4 text-gray-400" /></div>

          {/* Platform Fees */}
          <PLRow icon={CreditCard} label="Platform Fees" amount={summary.platform_fees}
            bgColor="bg-amber-50 hover:bg-amber-100" textColor="text-amber-600" iconColor="text-amber-600"
            onClick={() => openDetailDialog('platform_deductions')} clickable />

          <div className="flex items-center justify-center"><Minus className="w-4 h-4 text-gray-400" /></div>

          {/* Royalties */}
          <PLRow icon={Crown} label="Royalties" amount={summary.total_royalties}
            bgColor="bg-purple-50 hover:bg-purple-100" textColor="text-purple-600" iconColor="text-purple-600"
            onClick={() => openDetailDialog('royalty')} clickable />

          <div className="flex items-center justify-center"><Minus className="w-4 h-4 text-gray-400" /></div>

          {/* Head Office Expenses */}
          <PLRow icon={Building2} label="Head Office Expenses" amount={summary.head_office_expenses}
            bgColor="bg-indigo-50 hover:bg-indigo-100" textColor="text-indigo-600" iconColor="text-indigo-600"
            onClick={() => openDetailDialog('head_office_expenses')} clickable />

          <div className="flex items-center justify-center"><Equal className="w-4 h-4 text-gray-400" /></div>

          {/* Net Profit */}
          <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${summary.net_profit >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
            <div className="flex items-center gap-3">
              <TrendingUp className={`w-6 h-6 ${summary.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
              <div>
                <span className={`font-bold text-base ${summary.net_profit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                  Net Profit
                </span>
                <Badge variant="outline" className={`ml-2 text-xs ${summary.net_profit >= 0 ? 'border-emerald-400 text-emerald-700' : 'border-red-400 text-red-700'}`}>
                  {summary.net_profit_margin}%
                </Badge>
              </div>
            </div>
            <CurrencyDisplay amount={summary.net_profit} className={`text-xl font-bold ${summary.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const PLRow = ({ icon: Icon, label, amount, bgColor, textColor, iconColor, onClick, clickable }) => (
    <div
      className={`flex items-center justify-between p-3 rounded-lg ${bgColor} ${clickable ? 'cursor-pointer' : ''} transition-colors`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <span className={`font-medium text-sm ${textColor}`}>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <CurrencyDisplay amount={amount} className={`text-base font-bold ${textColor}`} />
        {clickable && <ChevronRight className={`w-4 h-4 ${textColor} opacity-50`} />}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="profit-loss-report">
      {/* Filters Card */}
      <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                Profit & Loss Report
              </CardTitle>
              <CardDescription className="text-blue-100">
                Revenue, expenses, staff wages, platform fees, royalties & HO expenses
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>Restaurant</Label>
              <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
                <SelectTrigger data-testid="pl-restaurant-filter">
                  <SelectValue placeholder="All Restaurants" />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants && restaurants.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Brand</Label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger data-testid="pl-brand-filter">
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 lg:col-span-2">
              <DateFilter onFilterChange={(filterData) => {
                if (filterData.filterType === 'exact') {
                  setStartDate(filterData.exactDate);
                  setEndDate(filterData.exactDate);
                } else {
                  setStartDate(filterData.startDate);
                  setEndDate(filterData.endDate);
                }
              }} />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={generateReport} disabled={loading}
              className="px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="generate-pl-report-btn"
            >
              {loading ? "Generating..." : "Generate Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* P&L Calculation + Summary Cards */}
      {reportData.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          <PLCalculationCard />
          <div className="grid gap-3 grid-cols-2 auto-rows-min">
            <SummaryCard title="Revenue" amount={summary.total_revenue} icon={TrendingUp} color="border-l-green-500" onClick={() => openDetailDialog('revenue')} />
            <SummaryCard title="Expenses" amount={summary.expenses_excl_wages} icon={Receipt} color="border-l-red-500" onClick={() => openDetailDialog('expense')} />
            <SummaryCard title="Staff Wages" amount={summary.staff_wages} icon={Users} color="border-l-orange-500" onClick={() => openDetailDialog('staff_wages')} />
            <SummaryCard title="Platform Fees" amount={summary.platform_fees} icon={CreditCard} color="border-l-amber-500" onClick={() => openDetailDialog('platform_deductions')} />
            <SummaryCard title="Royalties" amount={summary.total_royalties} icon={Crown} color="border-l-purple-500" onClick={() => openDetailDialog('royalty')} />
            <SummaryCard title="HO Expenses" amount={summary.head_office_expenses} icon={Building2} color="border-l-indigo-500" onClick={() => openDetailDialog('head_office_expenses')} />
          </div>
        </div>
      )}

      {/* Restaurant-wise Table */}
      {reportData.length > 0 && (
        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Restaurant-wise Breakdown
                </CardTitle>
                <CardDescription>{reportData.length} restaurant(s)</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={downloadExcel} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" /> Excel
                </Button>
                <Button onClick={downloadPDF} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" /> PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Restaurant</TableHead>
                    <TableHead className="text-right font-semibold text-green-700">Revenue</TableHead>
                    <TableHead className="text-right font-semibold text-red-700">Expenses</TableHead>
                    <TableHead className="text-right font-semibold text-orange-700">Staff Wages</TableHead>
                    <TableHead className="text-right font-semibold text-amber-700">Platform</TableHead>
                    <TableHead className="text-right font-semibold text-purple-700">Royalties</TableHead>
                    <TableHead className="text-right font-semibold text-emerald-700">Net Profit</TableHead>
                    <TableHead className="text-right font-semibold">Net %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.map((item, index) => (
                    <TableRow key={index} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetailDialog('revenue', item.restaurant_name)}
                    >
                      <TableCell className="font-medium">{item.restaurant_name}</TableCell>
                      <TableCell className="text-right text-green-600"><CurrencyDisplay amount={item.total_revenue} /></TableCell>
                      <TableCell className="text-right text-red-600"><CurrencyDisplay amount={item.expenses_excl_wages} /></TableCell>
                      <TableCell className="text-right text-orange-600"><CurrencyDisplay amount={item.staff_wages} /></TableCell>
                      <TableCell className="text-right text-amber-600"><CurrencyDisplay amount={item.platform_fees} /></TableCell>
                      <TableCell className="text-right text-purple-600"><CurrencyDisplay amount={item.total_royalties} /></TableCell>
                      <TableCell className={`text-right font-bold ${item.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        <CurrencyDisplay amount={item.net_profit} />
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${item.net_profit_margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {item.net_profit_margin}%
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Summary Row */}
                  <TableRow className="border-t-2 border-gray-300 bg-gray-100 font-bold">
                    <TableCell className="font-bold">TOTAL</TableCell>
                    <TableCell className="text-right text-green-700"><CurrencyDisplay amount={summary.total_revenue} /></TableCell>
                    <TableCell className="text-right text-red-700"><CurrencyDisplay amount={summary.expenses_excl_wages} /></TableCell>
                    <TableCell className="text-right text-orange-700"><CurrencyDisplay amount={summary.staff_wages} /></TableCell>
                    <TableCell className="text-right text-amber-700"><CurrencyDisplay amount={summary.platform_fees} /></TableCell>
                    <TableCell className="text-right text-purple-700"><CurrencyDisplay amount={summary.total_royalties} /></TableCell>
                    <TableCell className={`text-right ${summary.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      <CurrencyDisplay amount={summary.net_profit} />
                    </TableCell>
                    <TableCell className={`text-right ${summary.net_profit_margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {summary.net_profit_margin}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            {/* HO Expenses note */}
            {summary.head_office_expenses > 0 && (
              <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200 text-sm text-indigo-700">
                <Building2 className="w-4 h-4 inline mr-2" />
                Head Office Expenses: <CurrencyDisplay amount={summary.head_office_expenses} className="font-bold" /> (applies across all restaurants, deducted from overall Net Profit)
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {reportData.length === 0 && !loading && (
        <Card className="shadow-lg">
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Data Available</p>
              <p className="text-sm">Select filters and click "Generate Report" to view the P&L report</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 6-Tab Detail Dialog with Filters */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="pl-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>
                  {selectedRestaurantDetail
                    ? `${selectedRestaurantDetail} - Financial Breakdown`
                    : `Financial Breakdown`
                  }
                </DialogTitle>
                <DialogDescription>
                  {selectedRestaurantDetail
                    ? `Detailed view for ${selectedRestaurantDetail}`
                    : `Detailed breakdown of all entries`
                  }
                </DialogDescription>
              </div>
              <div className="flex gap-1.5">
                <Button onClick={downloadDetailedPDF} variant="outline" size="sm" className="gap-1.5 text-xs h-8 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="dialog-export-detailed-pdf">
                  <BookOpen className="w-3.5 h-3.5" /> Detailed PDF
                </Button>
                <Button onClick={() => downloadDialogExcel(detailType)} variant="outline" size="sm" className="gap-1.5 text-xs h-8" data-testid="dialog-export-excel">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                </Button>
                <Button onClick={() => downloadDialogPDF(detailType)} variant="outline" size="sm" className="gap-1.5 text-xs h-8" data-testid="dialog-export-pdf">
                  <FileText className="w-3.5 h-3.5" /> PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* Filters Row */}
          <div className="flex flex-wrap items-end gap-3 pb-3 border-b">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-gray-500 mb-1 block">Restaurant</Label>
              <Select value={dialogRestaurant} onValueChange={setDialogRestaurant}>
                <SelectTrigger className="h-8 text-xs" data-testid="dialog-filter-restaurant">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {getDialogFilterOptions(detailType).restaurants.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs text-gray-500 mb-1 block">{getCategoryLabel(detailType)}</Label>
              <Select value={dialogCategory} onValueChange={setDialogCategory}>
                <SelectTrigger className="h-8 text-xs" data-testid="dialog-filter-category">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {getDialogFilterOptions(detailType).categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-gray-500 mb-1 block">From Month</Label>
              <input
                type="month"
                value={dialogMonthFrom}
                onChange={(e) => setDialogMonthFrom(e.target.value)}
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm"
                data-testid="dialog-filter-month-from"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-gray-500 mb-1 block">To Month</Label>
              <input
                type="month"
                value={dialogMonthTo}
                onChange={(e) => setDialogMonthTo(e.target.value)}
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm"
                data-testid="dialog-filter-month-to"
              />
            </div>
            {hasActiveDialogFilters() && (
              <Button variant="ghost" size="sm" onClick={clearDialogFilters} className="h-8 gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="dialog-clear-filters">
                <X className="w-3 h-3" /> Clear
              </Button>
            )}
          </div>

          <Tabs value={detailType} onValueChange={(v) => { setDetailType(v); setDialogCategory("all"); }} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-6" data-testid="pl-detail-tabs">
              <TabsTrigger value="revenue" className="gap-1 text-xs">
                <TrendingUp className="w-3 h-3" /> Revenue
              </TabsTrigger>
              <TabsTrigger value="expense" className="gap-1 text-xs">
                <Receipt className="w-3 h-3" /> Expenses
              </TabsTrigger>
              <TabsTrigger value="staff_wages" className="gap-1 text-xs">
                <Users className="w-3 h-3" /> Wages
              </TabsTrigger>
              <TabsTrigger value="platform_deductions" className="gap-1 text-xs">
                <CreditCard className="w-3 h-3" /> Platform
              </TabsTrigger>
              <TabsTrigger value="royalty" className="gap-1 text-xs">
                <Crown className="w-3 h-3" /> Royalty
              </TabsTrigger>
              <TabsTrigger value="head_office_expenses" className="gap-1 text-xs">
                <Building2 className="w-3 h-3" /> HO Exp.
              </TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-auto mt-4">
              {/* Revenue Tab */}
              <TabsContent value="revenue" className="m-0">
                <BreakdownTable
                  data={getFilteredBreakdown('revenue')}
                  emptyIcon={TrendingUp}
                  emptyText="No revenue entries found"
                  columns={[
                    { key: 'date', label: 'Date', render: (item) => formatDateDDMonYYYY(item.date) },
                    { key: 'restaurant_name', label: 'Restaurant' },
                    { key: 'category_name', label: 'Category', badge: true, badgeColor: 'bg-green-50 text-green-700 border-green-200' },
                    { key: 'amount', label: 'Amount', align: 'right', currency: true, color: 'text-green-600' },
                  ]}
                />
              </TabsContent>

              {/* Expense Tab */}
              <TabsContent value="expense" className="m-0">
                <BreakdownTable
                  data={getFilteredBreakdown('expense')}
                  emptyIcon={Receipt}
                  emptyText="No expense entries found"
                  columns={[
                    { key: 'date', label: 'Date', render: (item) => formatDateDDMonYYYY(item.date) },
                    { key: 'restaurant_name', label: 'Restaurant' },
                    { key: 'category_name', label: 'Category', badge: true, badgeColor: 'bg-red-50 text-red-700 border-red-200' },
                    { key: 'vendor_name', label: 'Vendor' },
                    { key: 'amount', label: 'Amount', align: 'right', currency: true, color: 'text-red-600' },
                  ]}
                />
              </TabsContent>

              {/* Staff Wages Tab */}
              <TabsContent value="staff_wages" className="m-0">
                <BreakdownTable
                  data={getFilteredBreakdown('staff_wages')}
                  emptyIcon={Users}
                  emptyText="No staff wages entries found. Mark expense categories as 'Staff Wages' in Expense Categories settings."
                  columns={[
                    { key: 'date', label: 'Date', render: (item) => formatDateDDMonYYYY(item.date) },
                    { key: 'restaurant_name', label: 'Restaurant' },
                    { key: 'category_name', label: 'Category', badge: true, badgeColor: 'bg-orange-50 text-orange-700 border-orange-200' },
                    { key: 'vendor_name', label: 'Vendor' },
                    { key: 'amount', label: 'Amount', align: 'right', currency: true, color: 'text-orange-600' },
                  ]}
                />
              </TabsContent>

              {/* Platform Fees Tab */}
              <TabsContent value="platform_deductions" className="m-0">
                <BreakdownTable
                  data={getFilteredBreakdown('platform_deductions')}
                  emptyIcon={CreditCard}
                  emptyText="No platform deductions found"
                  columns={[
                    { key: 'month', label: 'Month', render: (item) => {
                      const [y, m] = (item.month || '').split('-');
                      return m ? `${MONTHS[parseInt(m)]}-${y}` : item.month;
                    }},
                    { key: 'restaurant_name', label: 'Restaurant' },
                    { key: 'platform_name', label: 'Platform', badge: true, badgeColor: 'bg-amber-50 text-amber-700 border-amber-200' },
                    { key: 'amount', label: 'Amount', align: 'right', currency: true, color: 'text-amber-600' },
                  ]}
                />
              </TabsContent>

              {/* Royalty Tab */}
              <TabsContent value="royalty" className="m-0">
                <BreakdownTable
                  data={getFilteredBreakdown('royalty')}
                  emptyIcon={Crown}
                  emptyText="No royalty entries found"
                  columns={[
                    { key: 'period', label: 'Period', render: (item) => `${MONTHS[item.month]} ${item.year}` },
                    { key: 'payee_name', label: 'Payee' },
                    { key: 'royalty_type', label: 'Type', badge: true, badgeColor: 'bg-purple-50 text-purple-700 border-purple-200' },
                    { key: 'restaurant_name', label: 'Restaurant' },
                    { key: 'amount', label: 'Amount', align: 'right', currency: true, color: 'text-purple-600' },
                  ]}
                />
              </TabsContent>

              {/* Head Office Expenses Tab */}
              <TabsContent value="head_office_expenses" className="m-0">
                <BreakdownTable
                  data={breakdowns.head_office_expenses || []}
                  emptyIcon={Building2}
                  emptyText="No head office expenses found for this period"
                  columns={[
                    { key: 'month', label: 'Month', render: (item) => {
                      const [y, m] = (item.month || '').split('-');
                      return m ? `${MONTHS[parseInt(m)]}-${y}` : item.month;
                    }},
                    { key: 'category', label: 'Category', badge: true, badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
                    { key: 'description', label: 'Description' },
                    { key: 'amount', label: 'Amount', align: 'right', currency: true, color: 'text-indigo-600' },
                  ]}
                />
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BreakdownTable = ({ data, emptyIcon: EmptyIcon, emptyText, columns }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <EmptyIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">{emptyText}</p>
      </div>
    );
  }
  
  const total = data.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.align === 'right' ? 'text-right' : ''}>{col.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, idx) => (
            <TableRow key={idx}>
              {columns.map((col) => (
                <TableCell key={col.key} className={col.align === 'right' ? `text-right font-medium ${col.color || ''}` : ''}>
                  {col.currency ? (
                    <CurrencyDisplay amount={item.amount} />
                  ) : col.badge ? (
                    <Badge variant="outline" className={col.badgeColor}>
                      {item[col.key]}
                    </Badge>
                  ) : col.render ? (
                    col.render(item)
                  ) : (
                    item[col.key]
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="p-3 bg-gray-50 border-t text-right text-sm font-semibold">
        Total: <CurrencyDisplay amount={total} className="font-bold" />
        <span className="text-gray-400 ml-2">({data.length} entries)</span>
      </div>
    </div>
  );
};

export default ProfitLossReport;
